import { useState, useEffect, useRef } from "react";
import { useRouter } from "next/navigation";
import { deleteCookie } from "cookies-next";
import useProfileStore from "@/store/useProfileStore";
import { COOKIE_CONFIG } from "@/config";
import { showDynamicToast, showToast, TOAST_TYPES } from "@/lib/toast-utils/toast.util";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useProfileData } from "@/components/hooks/profile/useProfileData.hook";
import { ProfileData } from "@/interface/profile.types";
import { clearProjectCreationStore } from "@/lib/store-utils/store.util";
import { logout } from "@/services/auth.service";

export const useProfileDropdown = () => {
  const router = useRouter();
  const queryClient = useQueryClient();
  const { profileData: storeProfileData, clearProfile } = useProfileStore();
  const { profileData, isLoading, error, refetch, isAuthenticated } =
    useProfileData();

  const [isMounted, setIsMounted] = useState<boolean>(false);
  const [isLoggingOut, setIsLoggingOut] = useState<boolean>(false);
  const [isOpen, setIsOpen] = useState<boolean>(false);
  const prevLoggingOutRef = useRef<boolean>(false);

  // Use profile data from the hook (which comes from API) or fallback to store data
  const currentProfileData = profileData || storeProfileData;

  // Cast profileData to the correct type
  const typedProfileData = currentProfileData as Partial<ProfileData> | null;

  // Generate fallback initials from name, username, or email
  const getFallbackInitials = () => {
    const name =
      typedProfileData?.name ||
      typedProfileData?.username ||
      typedProfileData?.email;
    if (!name) return "U";

    return name
      .split("@")[0] // Remove domain from email
      .split(" ")
      .map((item: string) => item[0])
      .join("")
      .toUpperCase()
      .slice(0, 2);
  };

  // Get display name (name, username, or email)
  const getDisplayName = () => {
    return (
      typedProfileData?.name ||
      typedProfileData?.username ||
      typedProfileData?.email ||
      "User"
    );
  };

  // Get display email (email or username as fallback)
  const getDisplayEmail = () => {
    return typedProfileData?.email || typedProfileData?.username || "No email";
  };

  // Get user type display
  const getUserType = () => {
    return typedProfileData?.userType;
  };

  // Get avatar image URL
  const getAvatarImageUrl = () => {
    return (typedProfileData as any)?.image?.url || "";
  };

  const logoutMutation = useMutation<void, unknown>({
    mutationFn: () => logout(),
    onSuccess: () => {
      // No-op: we already performed local cleanup when triggering logout
    },
    onError: () => {
      // Service already toasts an error; app state already cleared
    },
  });

  const triggerLogout = async (): Promise<void> => {
    // Set loading state immediately to show "Logging out..." message
    setIsLoggingOut(true);

    // Wait for React to render the loading state first
    await new Promise((resolve) => requestAnimationFrame(resolve));
    await new Promise((resolve) => requestAnimationFrame(resolve));

    // Fire API call in background (don't await - let it run in background)
    logoutMutation.mutate();

    // Wait for 10 seconds to show "Logging out..." state
    await new Promise((resolve) => setTimeout(resolve, 3000));

    // Perform local cleanup
    clearProjectCreationStore();
    localStorage.clear();
    deleteCookie(COOKIE_CONFIG.loggedIn);
    deleteCookie(COOKIE_CONFIG.profile);
    deleteCookie(COOKIE_CONFIG.version);
    clearProfile();
    showToast(TOAST_TYPES.success, "Logged out successfully");

    // Reset loading state before redirect
    setIsLoggingOut(false);
    router.replace("/login");
  };

  const changeRoute = (route: string) => {
    router.push(route);
  };

  const handleRetry = () => {
    refetch();
  };

  // Close dropdown when logout completes (isLoggingOut goes from true to false)
  useEffect(() => {
    if (prevLoggingOutRef.current && !isLoggingOut) {
      // Logout just completed, close dropdown
      setIsOpen(false);
    }
    prevLoggingOutRef.current = isLoggingOut;
  }, [isLoggingOut]);

  const handleLogout = () => {
    // Keep dropdown open during logout
    setIsOpen(true);
    triggerLogout();
  };

  useEffect(() => {
    setIsMounted(true);
  }, []);

  return {
    isMounted,
    profileData: typedProfileData,
    isLoading,
    error,
    logout: triggerLogout,
    handleLogout,
    changeRoute,
    handleRetry,
    isAuthenticated,
    // Computed values
    fallbackInitials: getFallbackInitials(),
    displayName: getDisplayName(),
    displayEmail: getDisplayEmail(),
    userType: getUserType(),
    avatarImageUrl: getAvatarImageUrl(),
    isLoggingOut: isLoggingOut, // Use state directly since we control the timing manually
    // Dropdown state
    isOpen,
    setIsOpen,
  };
};
